import { lazy, Suspense, useEffect, Component, type ComponentType, type ReactNode } from "react";
import { HelmetProvider } from "react-helmet-async";
import { initializeAnalyticsSession } from "@/lib/analytics-enhanced";

// Retry chunk imports with backoff before falling back to a full reload.
// Handles two failure modes from CDN deploys: (1) transient 404s at an edge POP
// during deploy propagation, and (2) negative-cached 404s that need a full
// reload to pick up a fresh index.html with new chunk hashes.
const RELOAD_KEY = "chunk-reload-ts";
const RELOAD_COOLDOWN_MS = 60_000;
const REFETCH_TIMEOUT_MS = 3_000;

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

async function importWithRetry<T>(importFn: () => Promise<T>): Promise<T> {
  const delays = [0, 600, 2000];
  let lastErr: unknown;
  for (const ms of delays) {
    if (ms) await delay(ms);
    try {
      return await importFn();
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr;
}

function readReloadTs(): number {
  try {
    return Number(sessionStorage.getItem(RELOAD_KEY)) || 0;
  } catch {
    return 0; // storage blocked (private mode, sandboxed frame) — no cooldown
  }
}

function writeReloadTs(ts: number): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(ts));
  } catch {
    /* see above — the cooldown just doesn't persist */
  }
}

// A hashed asset URL can end up holding an HTML body in the browser's HTTP
// cache: when the file is missing at the edge the SPA rewrite answers with
// index.html, and /assets/* carries `immutable`. A plain reload can never
// clear that — the browser does not re-request an immutable entry, and chunk
// hashes are content-derived, so the next deploy asks for the same URL and
// gets the same poison. Re-fetching with `cache: "reload"` is the way out: it
// ignores the stored entry, forces revalidation at the edge, and overwrites
// whatever was cached. Everything the document has already loaded is refetched
// because the failing chunk is not always identifiable from the error alone.
async function refetchAssetsFromNetwork(): Promise<void> {
  const urls = new Set(
    performance
      .getEntriesByType("resource")
      .map((entry) => entry.name)
      .filter((name) => name.startsWith(`${location.origin}/assets/`) && /\.(js|css)$/.test(name)),
  );
  await Promise.all([...urls].map((url) => fetch(url, { cache: "reload" }).catch(() => undefined)));
}

// Reload, but only after the poisoned entries have been evicted — otherwise
// the fresh page load reads the same broken chunk straight back out of cache.
function reloadWithFreshAssets(): void {
  const reload = () => window.location.reload();
  Promise.race([refetchAssetsFromNetwork(), delay(REFETCH_TIMEOUT_MS)]).then(reload, reload);
}

function maybeReload(): boolean {
  const last = readReloadTs();
  if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
  writeReloadTs(Date.now());
  reloadWithFreshAssets();
  return true;
}

function lazyWithRetry(importFn: () => Promise<{ default: ComponentType<any> }>) {
  return lazy(() =>
    importWithRetry(importFn).catch((err) => {
      if (maybeReload()) return new Promise(() => {}) as never;
      throw err;
    })
  );
}

// Vite fires this when a <link rel="modulepreload"> 404s — catches the case
// where the browser preloads a chunk that the edge POP doesn't have yet,
// before any dynamic import() actually runs.
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", (event) => {
    event.preventDefault();
    maybeReload();
  });
}

// A stale chunk after a deploy can surface as a *render* error (e.g. React.lazy
// reading `.default` of a module that 404'd to index.html) rather than a
// rejected import — which slips past the retry/preload handlers above. This
// top-level boundary catches that case and reloads to pick up a fresh
// index.html, instead of white-screening. Non-chunk errors get a refresh prompt.
const CHUNK_ERROR_RE =
  /dynamically imported module|module script|ChunkLoadError|Importing a module script failed|reading 'default'|text\/html|Loading chunk/i;
function isChunkError(err: unknown): boolean {
  const msg = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
  return CHUNK_ERROR_RE.test(msg);
}

// Keyed state, not a `key`: remounting the subtree on every path change would
// re-initialize CountyView (and its MapLibre map) on each county switch.
// `resetKey` is the pathname; a caught error is cleared when it changes, so
// Back, header links and in-app navigation reach a working page again.
export class RouteErrorBoundary extends Component<
  { children: ReactNode; resetKey?: string },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error) {
    if (isChunkError(error)) maybeReload();
    else console.error("App error boundary:", error);
  }
  componentDidUpdate(prevProps: { resetKey?: string }, prevState: { error: Error | null }) {
    // Only an error caught before this move. One the move itself threw is
    // caught in this very update, with `prevProps` still on the old path, and
    // clearing it would just render the failing route a second time.
    if (prevState.error && this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }
  private retry = () => this.setState({ error: null });
  render() {
    if (this.state.error) {
      const chunk = isChunkError(this.state.error);
      return (
        <div className="flex flex-col items-center justify-center h-screen gap-3 p-8 text-center">
          <p className="text-sm text-muted-foreground max-w-sm">
            {chunk ? "A new version of the site is available." : "Something went wrong."}
          </p>
          <div className="flex gap-2">
            {!chunk && (
              <button
                onClick={this.retry}
                className="inline-flex items-center gap-2 rounded border border-border px-4 py-2 text-sm font-semibold text-foreground hover:bg-muted"
              >
                Try again
              </button>
            )}
            <button
              onClick={reloadWithFreshAssets}
              className="inline-flex items-center gap-2 rounded bg-accent px-4 py-2 text-sm font-semibold text-accent-foreground hover:bg-accent/90"
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

/** The boundary, reset whenever the route's path changes. Must sit inside the router. */
export function RouteErrorReset({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <RouteErrorBoundary resetKey={pathname}>{children}</RouteErrorBoundary>;
}
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CheckoutHandler } from "@/components/CheckoutHandler";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { PostAuthRedirect } from "@/components/auth/PostAuthRedirect";
import { AuthLinkErrorHandler } from "@/components/auth/AuthLinkErrorHandler";
import { ScrollToTop } from "@/components/ScrollToTop";
import { RouteHistory } from "@/components/RouteHistory";

const LandingPage = lazyWithRetry(() => import("./pages/LandingPage"));
const StateView = lazyWithRetry(() => import("./pages/StateView"));
const CountyView = lazyWithRetry(() => import("./pages/CountyView"));
const SavedPropertiesPage = lazyWithRetry(() => import("./pages/SavedPropertiesPage"));
const Blog = lazyWithRetry(() => import("./pages/Blog"));
const BlogPost = lazyWithRetry(() => import("./pages/BlogPost"));
const Auth = lazyWithRetry(() => import("./pages/Auth"));
const Terms = lazyWithRetry(() => import("./pages/Terms"));
const Privacy = lazyWithRetry(() => import("./pages/Privacy"));
const Contact = lazyWithRetry(() => import("./pages/Contact"));
const About = lazyWithRetry(() => import("./pages/About"));
const AccountSettings = lazyWithRetry(() => import("./pages/AccountSettings"));
const MarketDashboard = lazyWithRetry(() => import("./pages/MarketDashboard"));
const NotFound = lazyWithRetry(() => import("./pages/NotFound"));
const AccountDeleted = lazyWithRetry(() => import("./pages/AccountDeleted"));
const Pricing = lazyWithRetry(() => import("./pages/Pricing"));
const LandClearingDemo = lazyWithRetry(() => import("./pages/LandClearingDemo"));
const OAuthConsent = lazyWithRetry(() => import("./pages/OAuthConsent"));

const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    initializeAnalyticsSession();
  }, []);

  return (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <RouteHistory />
            <CheckoutHandler />
            <PostAuthRedirect />
            <AuthLinkErrorHandler />
            <RouteErrorReset>
            <Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}>
              <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/map" element={<StateView />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/oauth/consent" element={<OAuthConsent />} />
                <Route path="/saved" element={<RequireAuth><SavedPropertiesPage /></RequireAuth>} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/about" element={<About />} />
                <Route path="/account" element={<RequireAuth><AccountSettings /></RequireAuth>} />
                <Route path="/market" element={<MarketDashboard />} />
                <Route path="/account-deleted" element={<AccountDeleted />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/land-clearing" element={<LandClearingDemo />} />
                <Route path="/:county" element={<CountyView />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
            </RouteErrorReset>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
  );
};

export default App;
